import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getAICoachUsage } from '../api/dataService';
import { useAuth } from './AuthContext';

// The context object
const CoachUsageContext = createContext({
  coachLimitReached: false,
  usageSeconds: 0,
  limitMinutes: 0,
  rewardedAds: null,
  isLoading: false,
  error: null,
  setCoachUsage: () => {},
  hydrateCoachUsage: async () => null,
  refreshCoachUsage: async () => null,
});

const toFiniteNumber = (value, fallback = 0) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const deriveRewardedAds = (rewardedAds) => {
  if (!rewardedAds || typeof rewardedAds !== 'object') {
    return null;
  }

  const stepsRequired = Math.max(0, Math.round(toFiniteNumber(rewardedAds.stepsRequired, 0)));
  const stepsCompleted = Math.max(0, Math.round(toFiniteNumber(rewardedAds.stepsCompleted, 0)));
  const creditSeconds = Math.max(0, Math.round(toFiniteNumber(rewardedAds.creditSeconds, 0)));

  if (!stepsRequired) {
    return null;
  }

  return {
    stepsRequired,
    stepsCompleted: Math.min(stepsRequired, stepsCompleted),
    creditSeconds,
  };
};

const deriveCoachUsage = ({ usageSeconds, limitMinutes, rewardedAds }) => {
  const nextUsageSeconds = toFiniteNumber(usageSeconds, 0);
  const nextLimitMinutes = toFiniteNumber(limitMinutes, 0);
  const coachLimitReached = (() => {
    if (nextLimitMinutes === -1) return false;
    if (nextLimitMinutes === 0) return nextUsageSeconds >= 0;
    return nextUsageSeconds >= nextLimitMinutes * 60;
  })();
  return {
    coachLimitReached,
    usageSeconds: nextUsageSeconds,
    limitMinutes: nextLimitMinutes,
    rewardedAds: deriveRewardedAds(rewardedAds),
  };
};

// Provider component
export const CoachUsageProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const userId = user?._id || user?.id;
  const [coachUsage, setCoachUsageState] = useState(() =>
    deriveCoachUsage({ usageSeconds: 0, limitMinutes: -1, rewardedAds: null })
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const limitMinutesRef = useRef(coachUsage.limitMinutes);

  // You can expose an updater for AICoachNew to call
  const setCoachUsageWrapper = useCallback(({ usageSeconds, limitMinutes, rewardedAds }) => {
    setCoachUsageState((prev) => {
      const derived = deriveCoachUsage({
        usageSeconds: usageSeconds ?? prev.usageSeconds,
        limitMinutes: limitMinutes ?? prev.limitMinutes,
        rewardedAds: rewardedAds ?? prev.rewardedAds,
      });
      limitMinutesRef.current = derived.limitMinutes;
      return derived;
    });
  }, []);

  const refreshCoachUsage = useCallback(async () => {
    if (authLoading || !user) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getAICoachUsage();
      const { usageSeconds, limitMinutes, rewardedAds } = response?.data || {};
      const nextUsageSeconds = toFiniteNumber(usageSeconds, 0);
      const nextLimitMinutes = toFiniteNumber(limitMinutes, 0);
      setCoachUsageWrapper({
        usageSeconds: nextUsageSeconds,
        limitMinutes: nextLimitMinutes,
        rewardedAds: rewardedAds ?? null,
      });
      return { usageSeconds: nextUsageSeconds, limitMinutes: nextLimitMinutes, rewardedAds: rewardedAds ?? null };
    } catch (err) {
      if (err?.response?.status === 429) {
        const knownLimitMinutes = limitMinutesRef.current;
        if (knownLimitMinutes !== -1) {
          setCoachUsageWrapper({
            usageSeconds: knownLimitMinutes * 60,
            limitMinutes: knownLimitMinutes,
          });
        }
      }
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, user, setCoachUsageWrapper]);

  useEffect(() => {
    const handler = () => {
      refreshCoachUsage();
    };
    window.addEventListener('forza:coachUsageRefresh', handler);
    return () => {
      window.removeEventListener('forza:coachUsageRefresh', handler);
    };
  }, [refreshCoachUsage]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setCoachUsageState(deriveCoachUsage({ usageSeconds: 0, limitMinutes: 0, rewardedAds: null }));
      limitMinutesRef.current = 0;
      setError(null);
      setIsLoading(false);
      return;
    }

    refreshCoachUsage();
  }, [authLoading, userId, refreshCoachUsage]);

  return (
    <CoachUsageContext.Provider
      value={{
        ...coachUsage,
        isLoading,
        error,
        setCoachUsage: setCoachUsageWrapper,
        hydrateCoachUsage: refreshCoachUsage,
        refreshCoachUsage,
      }}>
      {children}
    </CoachUsageContext.Provider>
  );
};

// Hook to use context
export const useCoachUsage = () => useContext(CoachUsageContext);
